import {reactive, ref} from "vue";
import {
    CHALLENGE_MAX_ATTEMPTS,
    VAULT_CLIENT_ID,
    clearChallengeToken,
    getChallengeToken,
    normalizeRecoveryCode,
    storeChallengeToken,
    withRefreshLock
} from "@/services/vault";

const AUTH_KEY = 'auth';

const loginFlow = () => ({
    step: 'credentials',
    codeType: 'google',
    useRecoveryCode: false,
    setup: null,
    recoveryCode: '',
    challengeExpiresAt: null,
    failedAttempts: 0,
    emailCodeSentAt: null
});

export default {



    install(app, options) {

        const templatePermissions = {
            mobileEnabled: false,
            hasDocuments: false,
            viewAgents: false
        };

        const header = {name: 'Authorization', prefix: 'Bearer ', ...options?.header};

        let refreshing = null;

        const auth = app.config.globalProperties.$auth = {
            user: reactive({
                uid: null,
                username: null,
                firstName: null,
                lastName: null,
                avatar: null,
                role: null
            }),
            permissions: reactive({...templatePermissions}),
            flow: reactive(loginFlow()),
            state: reactive({
                challengeExpired: false,
                sessionExpired: false
            }),
            authorized: false,
            loading: ref(false),
            appKey: ref(false),
            storage: app.config.globalProperties.$storage,
            http: app.config.globalProperties.$http,
            setTokens(token, refreshToken) {
                this.storage.set(AUTH_KEY, {token, refreshToken});
                this.applyToken(token);
            },
            clearTokens() {
                this.storage.remove(AUTH_KEY);
                this.applyToken(null);
                clearChallengeToken();
            },
            applyToken(token) {
                this.http.header('default', header.name, token ? header.prefix + token : null);
            },
            getAuthToken() {
                return this.storage.get(AUTH_KEY, {}).token ?? null;
            },
            getRefreshToken() {
                return this.storage.get(AUTH_KEY, {}).refreshToken ?? null;
            },
            bearer(token) {
                return token ? {Authorization: 'Bearer ' + token} : {};
            },
            setPermissions(permissions) {
                Object.assign(this.permissions, {...templatePermissions}, Object.fromEntries(permissions?.map(a => [a, true]) || []));
            },
            setUser(user) {
                this.user.uid = user.uid
                this.user.username = user.username;
                this.user.avatar = user.avatar;
                this.user.firstName = user.first_name;
                this.user.lastName = user.last_name;
                this.user.role = user.role;
                this.user.qrcode = user.qrcode;
            },
            setAppKey(key) {
                this.appKey.value = key;
            },
            isLoading() {
                return this.loading.value
            },
            check() {
                this.loading.value = true


                return new Promise((resolve, reject) => {

                    if (!this.getAuthToken() || !this.getRefreshToken()) {
                        this.clearTokens();
                        return reject();
                    }

                    this.applyToken(this.getAuthToken());

                    return this.http.request({name: 'vault', url: 'vault/auth/me', headers: this.bearer(this.getAuthToken())})
                        .then((response) => {
                            this.setPermissions(response.data.permissions);
                            this.setUser(response.data.user);
                            this.setAppKey(response.data.appKey);
                            this.authorized = true;
                            this.state.sessionExpired = false;


                            resolve();
                        })
                        .catch(() => {
                            if (this.getAuthToken()) {
                                this.clearTokens();
                                this.state.sessionExpired = true;
                            }

                            reject()
                        });
                }).finally(() => {
                    this.loading.value = false;
                });

            },
            vaultToken(data) {
                return this.http.request({
                    name: 'vault',
                    url: 'vault/oauth/token',
                    method: 'post',
                    refresh: false,
                    data: {client_id: VAULT_CLIENT_ID, scope: '', ...data}
                });
            },
            login(credentials) {
                this.clearTokens();
                this.resetLoginFlow();
                this.state.challengeExpired = false;
                this.state.sessionExpired = false;

                return this.vaultToken({grant_type: 'password', username: credentials.username, password: credentials.password})
                    .then((response) => this.handleTokenResponse(response.data));
            },
            handleTokenResponse(data) {
                if (data.recovery_code) {
                    this.flow.recoveryCode = data.recovery_code;
                }

                switch (data.auth_status) {
                    case 'VERIFIED':
                        this.setTokens(data.access_token, data.refresh_token);
                        clearChallengeToken();
                        this.resetLoginFlow();
                        break;
                    case 'REQUIRES_TWO_FACTOR_VERIFICATION':
                        this.startChallenge(data);
                        this.flow.codeType = data.two_factor_type === 'email' ? 'email' : 'google';
                        this.flow.emailCodeSentAt = data.two_factor_type === 'email' ? Date.now() : null;
                        this.flow.useRecoveryCode = false;
                        this.flow.step = 'verify';
                        break;
                    case 'REQUIRES_TWO_FACTOR_SETUP':
                        this.startChallenge(data);
                        this.flow.useRecoveryCode = false;
                        this.flow.setup = null;
                        this.flow.step = 'setup';
                        break;
                    default:
                        return Promise.reject({message: undefined, errors: [], code: 0});
                }

                return data.auth_status;
            },
            startChallenge(data) {
                if (!data.challenge_token) {
                    throw {message: undefined, errors: [], code: 0};
                }

                storeChallengeToken(data.challenge_token);
                this.flow.challengeExpiresAt = Date.now() + data.expires_in * 1000;
                this.flow.failedAttempts = 0;
            },
            resetLoginFlow() {
                Object.assign(this.flow, loginFlow());
            },
            cancelLogin() {
                this.clearTokens();
                this.resetLoginFlow();
            },
            challengeActive() {
                return !!getChallengeToken()
                    && this.flow.challengeExpiresAt !== null
                    && Date.now() < this.flow.challengeExpiresAt
                    && this.flow.failedAttempts < CHALLENGE_MAX_ATTEMPTS;
            },
            expireChallenge() {
                clearChallengeToken();
                this.resetLoginFlow();
                this.state.challengeExpired = true;
            },
            challengeRequest(params, countsAttempt = true) {
                if (!this.challengeActive()) {
                    this.expireChallenge();
                    return Promise.resolve(undefined);
                }

                return this.http.request({
                    name: 'vault',
                    method: 'post',
                    refresh: false,
                    ...params,
                    headers: this.bearer(getChallengeToken())
                })
                    .catch((error) => {
                        if (typeof error?.code === 'number' && error.code >= 400 && error.code < 500) {
                            if (countsAttempt) {
                                this.flow.failedAttempts++;
                            }

                            if (!countsAttempt || !this.challengeActive()) {
                                this.expireChallenge();
                                return undefined;
                            }
                        }

                        return Promise.reject(error);
                    });
            },
            verifyTwoFactor(code) {
                const recovery = this.flow.useRecoveryCode;

                return this.challengeRequest({
                    url: 'vault/oauth/token',
                    data: {
                        grant_type: 'password_verify',
                        client_id: VAULT_CLIENT_ID,
                        code_type: recovery ? 'recovery' : this.flow.codeType,
                        code: recovery ? normalizeRecoveryCode(code) : code,
                        scope: ''
                    }
                }).then((response) => response && this.handleTokenResponse(response.data));
            },
            setupTwoFactor() {
                return this.challengeRequest({url: 'vault/auth/2fa/setup', data: {type: 'google'}}, false)
                    .then((response) => {
                        if (!response) {
                            return response;
                        }

                        this.flow.setup = response.data.setup;
                        if (response.data.setup?.recovery_code) {
                            this.flow.recoveryCode = response.data.setup.recovery_code;
                        }

                        return response;
                    });
            },
            confirmTwoFactor(code) {
                return this.challengeRequest({url: 'vault/auth/2fa/confirm', data: {code, scope: ''}})
                    .then((response) => response && this.handleTokenResponse(response.data));
            },
            sendEmailCode() {
                return this.challengeRequest({url: 'vault/auth/2fa/email-code'}, false)
                    .then((response) => {
                        if (response) {
                            this.flow.emailCodeSentAt = Date.now();
                        }

                        return response;
                    });
            },
            refresh() {
                if (!refreshing) {
                    const usedRefreshToken = this.getRefreshToken();

                    refreshing = withRefreshLock(() => this.renewTokens(usedRefreshToken))
                        .finally(() => {
                            refreshing = null;
                        });
                }

                return refreshing;
            },
            renewTokens(usedRefreshToken) {
                const refreshToken = this.getRefreshToken();
                const token = this.getAuthToken();

                if (!refreshToken) {
                    return this.expireSession();
                }

                if (refreshToken !== usedRefreshToken && token) {
                    this.applyToken(token);
                    return Promise.resolve(token);
                }

                return this.vaultToken({grant_type: 'refresh_token', refresh_token: refreshToken})
                    .then((response) => {
                        if (!response.data.access_token) {
                            return this.expireSession();
                        }

                        this.setTokens(response.data.access_token, response.data.refresh_token ?? refreshToken);

                        return response.data.access_token;
                    }, () => this.expireSession());
            },
            expireSession() {
                this.clearTokens();
                this.state.sessionExpired = true;

                return Promise.reject({message: undefined, errors: [], code: 401});
            },
            logout() {
                const token = this.getAuthToken();
                const refreshToken = this.getRefreshToken();

                this.authorized = false;
                this.user.uid = null;
                this.user.username = null;
                this.user.qrcode = null;
                this.user.avatar = null;
                this.user.firstName = null;
                this.user.lastName = null;
                this.user.role = null;
                this.setPermissions([]);
                this.clearTokens();
                this.resetLoginFlow();

                if (!refreshToken) {
                    return Promise.resolve();
                }

                return this.http.request({
                    name: 'vault',
                    url: 'vault/oauth/revoke',
                    method: 'post',
                    refresh: false,
                    headers: this.bearer(token),
                    data: {client_id: VAULT_CLIENT_ID, token: refreshToken}
                }).catch(() => undefined);
            }
        };

        auth.http.onUnauthorized(() => auth.refresh());

        window.addEventListener('storage', (event) => {
            if (event.key !== AUTH_KEY && event.key !== null) {
                return;
            }

            const token = auth.getAuthToken();
            auth.applyToken(token);

            if (!token && auth.authorized) {
                auth.authorized = false;
                app.config.globalProperties.$router.push({name: 'login'});
            }
        });
    },

}
